/**
 * LibraryImagePicker.jsx
 * Choosing pictures that are already in Kreeboom.
 *
 * The gap it fills: every picture on this screen had to come off the disk,
 * even when the one you wanted was already in the library — so the way to
 * resize something you had saved was to download it first and upload it again.
 *
 * It hands back real files, so everything downstream treats them exactly like
 * pictures chosen from a folder.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Check, Warning } from './Icons.jsx';

/** Bounded for the same reason the file picker is: somebody has 900 pictures. */
const LIMIT = 60;

export default function LibraryImagePicker({ brandId, onClose, onChosen }) {
  const [media, setMedia] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { media: all } = await api.media.list(brandId);
      setMedia(all.filter((m) => m.kind === 'image' && !m.archived));
    } catch (e) {
      setError(e);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < LIMIT) next.add(id);
      return next;
    });

  /**
   * Fetches each chosen picture as a file.
   *
   * One at a time on purpose: sixty parallel requests for full-size images is
   * how a browser tab stops responding.
   */
  async function take() {
    const chosen = (media ?? []).filter((m) => picked.has(m.id));
    if (chosen.length === 0) return;

    setBusy(true);
    setError(null);
    const files = [];
    const failed = [];

    for (const item of chosen) {
      try {
        const response = await fetch(api.media.rawUrl(item.id));
        if (!response.ok) throw new Error(`${response.status}`);
        const blob = await response.blob();
        const name = item.file_name || item.name || `${item.id}.jpg`;
        files.push(new File([blob], name, { type: blob.type || item.mime_type || 'image/jpeg' }));
      } catch {
        failed.push(item.file_name || item.id);
      }
      setProgress({ done: files.length + failed.length, total: chosen.length });
    }

    setBusy(false);
    setProgress(null);

    if (files.length === 0) {
      setError(new Error('None of those could be fetched from the library.'));
      return;
    }
    onChosen(files, failed);
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <h2>Pictures already in Kreeboom</h2>
        <p className="hint">
          Choose any of this brand&rsquo;s pictures to resize, convert or put text on. The library
          copy is left exactly as it is.
        </p>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        {media === null ? (
          <p className="mono">Loading…</p>
        ) : media.length === 0 ? (
          <p className="hint">This brand has no pictures in its library yet.</p>
        ) : (
          <div className="library">
            {media.map((m) => (
              <button
                key={m.id}
                className={`lib-item lib-item--pick ${picked.has(m.id) ? 'is-picked' : ''}`}
                onClick={() => toggle(m.id)}
                title={m.file_name || m.id}
                type="button"
              >
                <img src={api.media.rawUrl(m.id)} alt={m.alt_text || ''} loading="lazy" />
                {picked.has(m.id) && (
                  <span className="lib-item__tick">
                    <Check size={15} />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {progress && (
          <p className="hint">
            Fetching {progress.done} of {progress.total}…
          </p>
        )}

        <div className="modal__actions">
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn--primary" onClick={take} disabled={busy || picked.size === 0}>
            {busy
              ? 'Fetching…'
              : `Use ${picked.size || ''} picture${picked.size === 1 ? '' : 's'}`.replace('  ', ' ')}
          </button>
        </div>
      </div>
    </div>
  );
}
